/**
 * The Different people tab's card — a decision the recruiter already made, and
 * the only place it can be unmade.
 *
 * 'Not the same person' writes a firm-wide registry pair, after which the
 * contact classifies NEW on every compare and its conflict card leaves the
 * view. That is the intended behaviour, and it is also why the decision has
 * been unreachable since it shipped: nothing rendered a contact whose conflict
 * had stopped being a conflict. This card is that surface.
 *
 * TWO STATEMENTS, deliberately separate. The verdict CONFIRMS what the
 * recruiter decided, so the card does not read as re-asking. The detail names
 * the NEW task that decision created — two records still share one LinkedIn
 * URL, so one of them is wrong — which nothing tracked before.
 *
 * The right-hand entries carry no Thrive NAME. The registry stores an id and an
 * attribution; resolving names would cost one Thrive call per pair on a poll
 * that runs every few seconds. The id and the deep link are the honest amount,
 * and the attribution ("ruled out by X on <date>") is what a firm-wide decision
 * owes the next person to read it.
 *
 * ------------------------------------------------------------------------
 * fu5 SUB-TASK 02, PART B — this card became a DECISION surface.
 *
 * It had ZERO decision options: only an Undo per registry pair, an archive of
 * something already settled. The two options below are ADDED, not reduced from
 * a longer set, and there are EXACTLY TWO because Jared closed the door on a
 * third (2026-09-08):
 *
 *   "There's no other option. We shouldn't add them if they don't clean it up.
 *    And if they clean it up, then we can add them for them. Those are really
 *    the only two options."
 *
 *   · "I fixed it in Thrive" — fires a re-check AGAINST THRIVE and branches on
 *     the real answer, never on the click.
 *   · "Skip"                 — the PERSISTED `leave-out` dismissal.
 *
 * `Undo this decision` STAYS, and stays visually subordinate to those two rows
 * (a small footer button, aligned to the trailing edge) so the card does not
 * read as offering three choices. It is not a third option in Jared's sense: it
 * reverses the EARLIER 'Not the same person' call rather than answering *what
 * do we do now*, and removing it would strand a firm-wide permanent decision
 * with no reversal surface anywhere in the product — the exact hole this card
 * was built to close. Whether it should move out of the decision block entirely
 * is FLAGGED FOR THE OPERATOR and deliberately not guessed at here.
 */

import type { DifferentPersonDecision } from "@nu-assist/contracts";
import { type HighlightOption, HighlightOptionGroup } from "@nu-assist/ui";
import type { ReactNode } from "react";
import { thriveContactUrl } from "../../lib/thriveLinks";
import { ConflictCardShell } from "./ConflictCardShell";
import {
  CONFLICT_ACTION_CAPTION,
  DIFFERENT_PERSON_DETAIL,
  DIFFERENT_PERSON_VERDICT,
  DUPLICATE_LINKEDIN_ACTION_SENTENCES,
  type DuplicateLinkedInAction,
  LEAVE_OUT_HELPER,
  LEFT_OUT_PILL,
  OPEN_THRIVE_RECORD_LABEL,
  RECHECK_CHECK_FAILED,
  RECHECK_CLEARED_NOTE,
  RECHECK_CLEARED_VERDICT,
  RECHECK_CONDITION_DIFFERENT_PERSON,
  RECHECK_PENDING_TITLE,
  UNDO_DIFFERENT_PERSON_LABEL,
  UNDO_LEAVE_OUT_LABEL,
  differentPersonAttribution,
  formatIgnoredDate,
} from "./conflictActionsCopy";

/** What the last re-check said for this row, or `null` if none has run.
 *
 *  THREE VALUES, NOT A BOOLEAN. `check_failed` is neither outcome: not
 *  resolved, not still-colliding — *unknown*. Collapsing it into
 *  "not resolved" is what makes a card shake at somebody because Thrive was
 *  rate-limited. */
export type RecheckOutcome = "cleared" | "still_colliding" | "check_failed";

export interface DifferentPersonCardProps {
  decision: DifferentPersonDecision;
  /** Presigned S3 GET URL for the SOURCE row's stored avatar. */
  resolvedAvatarUrl?: string | null;
  /** Undo ONE pair. Absent when the parent cannot persist (no api/importId). */
  onUndo?: (thriveId: string) => void;
  /** Inline error from a failed Undo. */
  error?: string | null;
  /**
   * The selected decision, or `null` for UNDECIDED — the state this card starts
   * in and, for two of the three re-check outcomes, the state it STAYS in.
   *
   * Only a `cleared` verdict decides the row. `still_colliding` and
   * `check_failed` both leave this null: the recruiter has not answered the
   * question yet, and pretending otherwise would let an unfixed collision ride
   * to export looking answered.
   */
  action?: DuplicateLinkedInAction | null;
  /** Reports a click on a non-selected option. The parent owns the semantics:
   *  `fixedInThrive` fires the Thrive re-check, `skipForNow` calls
   *  `PUT .../leave-out`. Absent → the options do not render. */
  onActionChange?: (action: DuplicateLinkedInAction) => void;
  /** True while the re-check is in flight. BOTH options go disabled: one click
   *  is one Thrive read, and a re-clickable row is how one recruiter becomes N
   *  reads on one contact. */
  recheckPending?: boolean;
  /** The last re-check's verdict for this row. */
  recheckOutcome?: RecheckOutcome | null;
  /** Changes on every re-check answer, and rides down to the option group as
   *  its shake `nonce` — which is what makes a REPEATED `still_colliding`
   *  wobble a second time. See the counter's note in `CompareToThriveModal`. */
  shakeKey?: number;
  /** The server's own recruiter-safe line on `check_failed` (non-null only
   *  there). Falls back to `RECHECK_CHECK_FAILED` when absent. */
  recheckDetail?: string | null;
  /**
   * Rendered in the shell's celebration slot. A NODE, not a flag: the card must
   * not learn which feature flag reveals a celebration, and the parent is where
   * the response's `cleared` branch and `?gamification=off` are both
   * already in scope.
   */
  celebration?: ReactNode;
  /** True when this row carries a persisted dismissal — the archive shape. The
   *  decision options do not render; the leave-out Undo does. Mirrors
   *  `W2ConflictCard`'s own left-out branch so a skipped row looks the same
   *  wherever it was skipped from. */
  leftOut?: boolean;
  /** When the dismissal was recorded — the "skipped <date>" line. */
  leftOutAt?: string | null;
  /** Undo the leave-out (restores the row to the pending section). */
  onUndoLeaveOut?: () => void;
}

function fullName(first?: string | null, last?: string | null): string {
  return [first, last].filter(Boolean).join(" ").trim();
}

/** Date AND time, on the attribution line — the same rule the ignored date
 *  follows. Who decided and exactly when is the whole value of an attribution
 *  on a decision everyone in the firm is bound by. */
function formatDecidedAt(at: Date, now: Date): string {
  const opts: Intl.DateTimeFormatOptions =
    at.getFullYear() === now.getFullYear()
      ? { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }
      : { year: "numeric", month: "short", day: "numeric" };
  return at.toLocaleString(undefined, opts);
}

/** Per-option `data-testid`, in ONE place so the card, its test and any future
 *  E2E cannot drift on the naming. */
const DUPLICATE_ACTION_TEST_IDS: Record<DuplicateLinkedInAction, string> = {
  fixedInThrive: "fixed-in-thrive",
  skipForNow: "skip-for-now",
};

export function DifferentPersonCard({
  decision,
  resolvedAvatarUrl,
  onUndo,
  error = null,
  action = null,
  onActionChange,
  recheckPending = false,
  recheckOutcome = null,
  shakeKey = 0,
  recheckDetail = null,
  celebration,
  leftOut = false,
  leftOutAt = null,
  onUndoLeaveOut,
}: DifferentPersonCardProps) {
  const now = new Date();
  const id = decision.import_contact_id;
  const excluded = decision.excluded ?? [];
  const cleared = recheckOutcome === "cleared";

  // THE FIX-IT-ON-THE-SPOT ROUTE, in the option's `trailing` slot — OUTSIDE the
  // activating element, never inside the label. A label spanning the full row
  // swallows the gaps around an interactive child, and over the link itself the
  // browser fires BOTH (the same rule `CompanyChangeRow`'s label follows).
  //
  // Only with EXACTLY ONE ruled-out pair. With several there is no single
  // "the Thrive record" to open, and a link that silently picks the first would
  // be guessing on the recruiter's behalf; the shell's right-hand rows already
  // carry one deep link EACH, built from the same `thriveContactUrl()` helper,
  // and those stay correct at any count. The helper exists because this domain
  // had been copy-pasted three times — do not hardcode it a fourth.
  const soleThriveId = excluded.length === 1 ? excluded[0]?.thrive_id : undefined;
  const openInThrive = soleThriveId ? (
    <a
      data-testid={`different-person-open-thrive-${id}`}
      href={thriveContactUrl(soleThriveId)}
      target="_blank"
      rel="noreferrer"
      className="whitespace-nowrap rounded-md border border-border bg-bg px-2 py-1 text-caption text-accent hover:bg-surface hover:underline"
    >
      {OPEN_THRIVE_RECORD_LABEL}
    </a>
  ) : undefined;

  const options: HighlightOption<DuplicateLinkedInAction>[] = [
    {
      value: "fixedInThrive",
      label: DUPLICATE_LINKEDIN_ACTION_SENTENCES.fixedInThrive,
      // WHAT WILL BE CHECKED, under the action rather than inside it. This
      // card's label is now word-for-word `W1ConflictCard`'s — once neither
      // names a method, "I went and changed data in Thrive" is the same
      // sentence on both — and the THRESHOLD is where they genuinely differ.
      // NONE here, because this row is about to be CREATED alongside whatever
      // remains; one survivor on the pile-up card, where a correct merge leaves
      // exactly that.
      description: RECHECK_CONDITION_DIFFERENT_PERSON,
      trailing: openInThrive,
      // One click, one Thrive read. Disabling in flight is the mechanism; the
      // parent's own in-flight guard is the belt to this pair of braces.
      disabled: recheckPending,
      title: recheckPending ? RECHECK_PENDING_TITLE : undefined,
      testId: `different-person-${DUPLICATE_ACTION_TEST_IDS.fixedInThrive}-${id}`,
    },
    {
      value: "skipForNow",
      label: DUPLICATE_LINKEDIN_ACTION_SENTENCES.skipForNow,
      disabled: recheckPending,
      title: recheckPending ? RECHECK_PENDING_TITLE : undefined,
      testId: `different-person-${DUPLICATE_ACTION_TEST_IDS.skipForNow}-${id}`,
    },
  ];

  // The `skipped` pill + date, identical to `W2ConflictCard`'s: a contact
  // dismissed from this section and one dismissed from a conflict card are the
  // same state, and an archive that renders them differently is hard to read.
  const banner = leftOut ? (
    <div className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-2">
        <span
          data-testid="different-person-left-out-pill"
          className="rounded-full border border-border bg-bg px-2 py-0.5 text-caption text-text-secondary"
        >
          {LEFT_OUT_PILL}
        </span>
        <span className="text-caption text-text-secondary">{LEAVE_OUT_HELPER}</span>
      </div>
      {leftOutAt && (
        <span
          data-testid={`different-person-ignored-date-${id}`}
          className="text-caption text-text-secondary"
        >
          {formatIgnoredDate(new Date(leftOutAt), now)}
        </span>
      )}
    </div>
  ) : null;

  const decisionBlock =
    onActionChange && !leftOut ? (
      <div className="flex flex-col gap-1.5">
        <p className="text-caption text-text-secondary">{CONFLICT_ACTION_CAPTION}</p>
        <HighlightOptionGroup<DuplicateLinkedInAction>
          aria-label="Duplicate LinkedIn URL action"
          data-testid={`different-person-actions-${id}`}
          options={options}
          // `null` = UNDECIDED, and the primitive renders it legally. Two of
          // the three re-check outcomes leave it null on purpose.
          value={action}
          onChange={onActionChange}
          // The rejection lands on the ANSWER the recruiter gave — the macOS
          // password reading (operator, 2026-09-10). `check_failed` is absent
          // on purpose: a read that could not RUN is not a "no", and wobbling
          // at somebody because Thrive was down blames the wrong party.
          shake={
            recheckOutcome === "still_colliding"
              ? { value: "fixedInThrive", nonce: shakeKey }
              : null
          }
        />
        {/* THE VERDICT LINE FOR THE LAST CHECK, if one has run.
            `still_colliding` deliberately has NO line here: it is carried by
            the shake and the toast, and a third copy of the same sentence
            sitting permanently under the option would turn a moment into an
            accusation the recruiter has to keep looking at. */}
        {/* THE HONEST HALF of the cleared state. The verdict line above the
            card has already flipped to `RECHECK_CLEARED_VERDICT`; this is the
            part that stops the re-render from being a lie. The endpoint
            PERSISTS NOTHING — retiring the registry pair on a zero-match
            observation is exactly what `stale_registry_pairs` refuses to do,
            and the pair stays semantically true — so `get_compare` re-derives
            the collision on the next load and this card comes back. Saying so
            beats faking durability in the client. */}
        {cleared && (
          <p
            data-testid={`different-person-cleared-${id}`}
            className="text-caption text-text-secondary"
          >
            {RECHECK_CLEARED_NOTE}
          </p>
        )}
        {/* `<output>` — implicit role `status`, a POLITE live region. Not
            `alert`: nothing here is the recruiter's doing, and an assertive
            interruption would frame a Thrive rate limit as their mistake. */}
        {recheckOutcome === "check_failed" && (
          <output
            data-testid={`different-person-check-failed-${id}`}
            className="text-label text-text-secondary"
          >
            {recheckDetail || RECHECK_CHECK_FAILED}
          </output>
        )}
      </div>
    ) : null;

  return (
    <ConflictCardShell
      anchorId={id}
      testId="different-person-card"
      source={{
        name: fullName(decision.first_name, decision.last_name) || "This row",
        company: decision.company,
        title: decision.title,
        linkedinUrl: decision.linkedin_url,
        avatarUrl: decision.profile_image_s3_key ? resolvedAvatarUrl : undefined,
      }}
      thrive={excluded.map((ref) => ({
        thriveId: ref.thrive_id,
        note: differentPersonAttribution(
          ref.created_by,
          formatDecidedAt(new Date(ref.created_at), now)
        ),
      }))}
      // THE ROW RE-RENDERS IN ITS NEW STATE on a `cleared` verdict — that is
      // the outcome the re-check exists to produce, and leaving the old line
      // ("exporting will create a second Thrive contact on this LinkedIn URL")
      // above a card whose URL is now free would state the opposite of what we
      // just observed. The two other outcomes change nothing here: the card is
      // still what it was.
      verdict={cleared ? RECHECK_CLEARED_VERDICT : DIFFERENT_PERSON_VERDICT}
      // Structured, not one paragraph: the note has a heading, a "To fix it:"
      // step and a closing line, and its whole job is to answer "what do I do
      // with this contact" — a wall of prose answers that no better than the
      // warning it would be restating.
      verdictDetail={
        <span className="flex flex-col gap-1.5">
          <span className="font-semibold">{DIFFERENT_PERSON_DETAIL.title}</span>
          <span>{DIFFERENT_PERSON_DETAIL.body}</span>
          <span>
            <span className="font-semibold">{DIFFERENT_PERSON_DETAIL.fixLabel} </span>
            {DIFFERENT_PERSON_DETAIL.fix}
          </span>
          <span>{DIFFERENT_PERSON_DETAIL.selfClearing}</span>
        </span>
      }
      // The same note, flattened, for the screen reader — built from the same
      // constant so the two surfaces cannot drift.
      verdictDetailText={[
        DIFFERENT_PERSON_DETAIL.title,
        DIFFERENT_PERSON_DETAIL.body,
        `${DIFFERENT_PERSON_DETAIL.fixLabel} ${DIFFERENT_PERSON_DETAIL.fix}`,
        DIFFERENT_PERSON_DETAIL.selfClearing,
      ].join(" ")}
      verdictTone="resolved"
      // SHAKES ONLY ON `still_colliding`. Never on `check_failed`: a read that
      // could not run is *unknown*, and shaking there blames a recruiter for a
      // Thrive rate limit. Never on `cleared` either, obviously.
      shake={recheckOutcome === "still_colliding"}
      celebration={celebration}
      banner={banner}
      dimmed={leftOut}
      footer={
        decisionBlock || onUndo || onUndoLeaveOut || error ? (
          <div className="flex flex-col gap-3">
            {decisionBlock}
            {/* A skipped row swaps its controls for the leave-out Undo, exactly
                as a skipped conflict card does. The registry Undo is hidden
                here rather than stacked beside it: two buttons both reading
                "undo" on one dimmed card cannot be told apart, and the
                registry decision is still reachable — un-skip first. */}
            {leftOut && onUndoLeaveOut ? (
              <div className="flex justify-end">
                <button
                  type="button"
                  data-testid={`different-person-undo-leave-out-${id}`}
                  className="rounded-md border border-border bg-bg px-2 py-1 text-label text-text hover:bg-surface"
                  onClick={onUndoLeaveOut}
                >
                  {UNDO_LEAVE_OUT_LABEL}
                </button>
              </div>
            ) : null}
            {/* VISUALLY SUBORDINATE TO THE TWO OPTION ROWS, on purpose: a
                trailing-aligned bordered button under a full-width highlight
                group does not read as a third choice. It answers a different
                question — "take back the earlier call" — and the card would
                offer no reversal of a firm-wide permanent decision without it.
                Whether it belongs outside the decision block entirely is an
                OPEN question flagged for the operator; it is left where it
                was. */}
            {!leftOut && onUndo ? (
              <div className="flex flex-col items-end gap-1">
                {/* One Undo per PAIR, not one per card: a URL can be ruled out
                    against several Thrive contacts, and those are separate
                    decisions that separate people may have made. A single
                    button would retire all of them on one click. With exactly
                    one pair — the ordinary case — the id is dropped from the
                    label, since there is nothing to disambiguate. */}
                {excluded.map((ref) => (
                  <button
                    key={ref.thrive_id}
                    type="button"
                    data-testid={`different-person-undo-${id}-${ref.thrive_id}`}
                    className="rounded-md border border-border bg-bg px-2 py-1 text-label text-text hover:bg-surface"
                    onClick={() => onUndo(ref.thrive_id)}
                  >
                    {excluded.length === 1
                      ? UNDO_DIFFERENT_PERSON_LABEL
                      : `${UNDO_DIFFERENT_PERSON_LABEL} (#${ref.thrive_id})`}
                  </button>
                ))}
              </div>
            ) : null}
            {error ? (
              <p
                data-testid={`different-person-error-${id}`}
                className="self-end text-label text-error-ink"
                role="alert"
              >
                {error}
              </p>
            ) : null}
          </div>
        ) : null
      }
    />
  );
}
